export const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY || '';

let isLoadingScript = false;
let scriptLoadPromise: Promise<void> | null = null;

const unrefTimer = (timer: unknown) => {
  if (typeof timer === 'object' && timer !== null && 'unref' in timer && typeof timer.unref === 'function') {
    timer.unref();
  }
};

/**
 * Load Google Maps API script dynamically if not already loaded
 * Uses the same loading pattern as @vis.gl/react-google-maps to avoid conflicts
 */
const ensureGoogleMapsLoaded = (): Promise<void> => {
  if (window.google?.maps?.Geocoder) {
    return Promise.resolve();
  }

  if (isLoadingScript && scriptLoadPromise) {
    return scriptLoadPromise;
  }

  // Check if script is already in the DOM (loaded by @vis.gl/react-google-maps)
  const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
  if (existingScript) {
    // Script exists, wait for it to load
    isLoadingScript = true;
    scriptLoadPromise = new Promise((resolve, reject) => {
      const maxAttempts = 100; // 10 seconds total
      let attempts = 0;

      const checkInterval = setInterval(() => {
        attempts++;
        if (window.google?.maps?.Geocoder) {
          clearInterval(checkInterval);
          isLoadingScript = false;
          resolve();
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval);
          isLoadingScript = false;
          reject(new Error('Timeout waiting for Google Maps API to load'));
        }
      }, 100);

      unrefTimer(checkInterval);
    });
    return scriptLoadPromise;
  }

  // No script found, load it ourselves with the same format as @vis.gl/react-google-maps
  isLoadingScript = true;
  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      isLoadingScript = false;
      // Give it a moment for the API to fully initialize
      const initDelayTimer = setTimeout(() => {
        if (window.google?.maps?.Geocoder) {
          resolve();
        } else {
          reject(new Error('Google Maps API loaded but Geocoder not available'));
        }
      }, 100);

      unrefTimer(initDelayTimer);
    };
    script.onerror = () => {
      isLoadingScript = false;
      reject(new Error('Failed to load Google Maps API'));
    };
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
};

/**
 * Get geolocation coordinates from an address string using Google Maps Geocoding API
 *
 * Note: This requires the following APIs to be enabled in Google Cloud Console:
 * - Maps JavaScript API (for the map component)
 * - Geocoding API (for address-to-coordinates conversion)
 *
 * @param address - The address string to geocode
 * @returns Coordinates object with lat/lng, or null if geocoding fails
 */
export const getGeoPositionByAddress = async (address: string) => {
  if (!address) {
    return null;
  }

  try {
    await ensureGoogleMapsLoaded();

    if (!window.google?.maps?.Geocoder) {
      console.warn('Google Maps Geocoder not available');
      return null;
    }

    const geocoder = new window.google.maps.Geocoder();
    const response = await geocoder.geocode({ address });

    if (response.results && response.results.length > 0) {
      const location = response.results[0].geometry.location;
      return {
        lat: location.lat(),
        lng: location.lng(),
      };
    }

    return null;
  } catch (error: any) {
    if (error?.message?.includes('REQUEST_DENIED')) {
      console.error(
        'Geocoding failed: REQUEST_DENIED. Please ensure:\n' +
          '1. The Geocoding API is enabled in Google Cloud Console\n' +
          '2. The API key has proper restrictions configured\n' +
          '3. Billing is enabled for your Google Cloud project',
      );
    } else if (error?.message?.includes('Timeout')) {
      console.warn('Google Maps API is taking longer than expected to load. The map may still be initializing.');
    } else {
      console.warn('Geocoding error:', error);
    }
    return null;
  }
};
